import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { CompanyDocumentsFormComponent } from '../../components/company-documents-form/company-documents-form.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';

import { CompanyDocument } from '../../../../models/entities/company-document';

@Component({
  selector: 'app-company-documents-edit-page',
  imports: [LoaderComponent, CompanyDocumentsFormComponent],
  templateUrl: './company-documents-edit-page.component.html',
  styleUrl: './company-documents-edit-page.component.scss',
})
export class CompanyDocumentsEditPageComponent implements OnInit, OnDestroy {

  companyDocumentId?: number;
  companyDocument?: CompanyDocument;
  isEditMode: boolean = false;

  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };

  private readonly LOADER_CONTEXT = 'company-documents-edit-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService
  ) { }

  ngOnInit(): void {
    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    const paramId = this.route.snapshot.paramMap.get('companyDocumentId');
    if (paramId) {
      this.companyDocumentId = Number(paramId);
      this.isEditMode = true;
      this.loadCompanyDocument(this.companyDocumentId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get code(): string {
    return this.companyDocument?.code ?? '';
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Update Company Document' : 'New Company Document';
  }

  onFormSavedData(companyDocument: CompanyDocument): void {
    const isFirstSave = !this.isEditMode;

    this.companyDocument = companyDocument;
    this.companyDocumentId = companyDocument.companyDocumentId ?? this.companyDocumentId;

    if (isFirstSave && this.companyDocumentId) {
      this.routeService.companyDocumentsDetail(Number(this.companyDocumentId));
    }
  }

  private loadCompanyDocument(companyDocumentId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailCompanyDocument(companyDocumentId).subscribe({
      next: (response: any) => {
        const companyDocument: CompanyDocument | null =
          this.qualityManagementService.readResponseKey(response, 'companyDocument') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!companyDocument) {
          this.alertNotificationService.error('Company document not found.');
          return;
        }

        this.companyDocument = companyDocument;
      },
      error: (error: any) => {
        console.error('Error loading the company document:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to load the company document.')
        );
      }
    });
  }
}
